import {
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  OnChanges,
  Output,
  QueryList,
  SimpleChanges,
  ViewChild,
  ViewChildren
} from '@angular/core';

@Component({
  selector: 'app-central-pedidos-dialog',
  templateUrl: './central-pedidos-dialog.component.html',
  styleUrls: ['./central-pedidos-dialog.component.scss']
})
export class CentralPedidosDialogComponent implements OnChanges {
  @Input() aberto = false;
  @Input() termoBusca = '';
  @Input() buscando = false;
  @Input() erro = '';
  @Input() clientesAgrupados: any[] = [];
  @Input() clienteSelecionado: any = null;
  @Input() enderecos: any[] = [];
  @Input() enderecoSelecionado: any = null;
  @Input() preparando = false;
  @Input() lojaDestino: any = null;
  @Input() roteamento: any = null;
  @Input() lojasParticipantes: any[] = [];

  @Output() fechar = new EventEmitter<void>();
  @Output() buscar = new EventEmitter<void>();
  @Output() alterarTermoBusca = new EventEmitter<string>();
  @Output() selecionarCliente = new EventEmitter<any>();
  @Output() trocarCliente = new EventEmitter<void>();
  @Output() selecionarEndereco = new EventEmitter<any>();
  @Output() selecionarLojaSemEndereco = new EventEmitter<any>();
  @Output() cadastrarEndereco = new EventEmitter<any>();

  @ViewChild('buscaCentralInput') buscaCentralInput?: ElementRef<HTMLInputElement>;
  @ViewChildren('clienteCard') clienteCards?: QueryList<ElementRef<HTMLButtonElement>>;
  @ViewChildren('enderecoCard') enderecoCards?: QueryList<ElementRef<HTMLButtonElement>>;

  clienteIndiceAtivo = -1;
  enderecoIndiceAtivo = -1;

  ngOnChanges(changes: SimpleChanges) {
    if (changes.clientesAgrupados) {
      this.clienteIndiceAtivo = this.totalClientes ? 0 : -1;
    }

    if (changes.enderecos) {
      this.enderecoIndiceAtivo = this.enderecos?.length ? 0 : -1;
    }

    if (changes.aberto && this.aberto) {
      setTimeout(() => this.foqueBusca(), 0);
    }
  }

  get etapaEnderecos(): boolean {
    return !!this.clienteSelecionado;
  }

  get podeBuscar(): boolean {
    return !this.buscando && !this.preparando && (this.termoBusca || '').trim().length >= 2;
  }

  get totalClientes(): number {
    return (this.clientesAgrupados || []).reduce((total, grupo) => total + ((grupo?.clientes || []).length), 0);
  }

  onTermoBuscaChange(termo: string) {
    this.clienteIndiceAtivo = -1;
    this.alterarTermoBusca.emit(termo);
  }

  onBuscar() {
    if (!this.podeBuscar) return;
    this.buscar.emit();
  }

  onSelecionarClientePorClique(cliente: any, indice: number) {
    this.clienteIndiceAtivo = indice;
    this.selecionarCliente.emit(cliente);
  }

  onSelecionarEnderecoPorClique(endereco: any, indice: number) {
    this.enderecoIndiceAtivo = indice;
    this.selecionarEndereco.emit(endereco);
  }

  onSelecionarLojaSemEndereco(loja: any) {
    if (this.preparando) return;
    if (this.lojaRequerEndereco(loja)) {
      this.cadastrarEndereco.emit(loja);
      return;
    }
    if (!this.lojaPodeContinuarSemEndereco(loja)) return;
    this.selecionarLojaSemEndereco.emit(loja);
  }

  onContinuarLojaSemEndereco() {
    const loja = this.lojaDestinoVisual;
    if (!loja || !this.lojaPodeContinuarSemEndereco(loja) || this.preparando) return;
    this.selecionarLojaSemEndereco.emit(loja);
  }

  onTrocarCliente() {
    this.clienteIndiceAtivo = this.totalClientes ? 0 : -1;
    this.enderecoIndiceAtivo = -1;
    this.trocarCliente.emit();
    setTimeout(() => this.foqueBusca(), 0);
  }

  onFechar() {
    if (this.preparando) return;
    this.fechar.emit();
  }

  @HostListener('document:keydown', ['$event'])
  onKeyDown(event: KeyboardEvent) {
    if (!this.aberto || this.preparando) return;

    if (event.key === 'Escape') {
      event.preventDefault();
      this.onFechar();
      return;
    }

    if (this.etapaEnderecos) {
      this.onKeyDownEnderecos(event);
      return;
    }

    if (event.key === 'F4') {
      event.preventDefault();
      this.foqueBusca();
      return;
    }

    if (event.altKey && this.selecioneClientePorAtalho(event)) {
      return;
    }

    if (event.key === 'ArrowDown') {
      event.preventDefault();
      this.movaClienteAtivo(1);
      return;
    }

    if (event.key === 'ArrowUp') {
      event.preventDefault();
      this.movaClienteAtivo(-1);
      return;
    }

    if (event.key === 'Enter') {
      event.preventDefault();
      this.confirmeClienteOuBusca();
    }
  }

  getClientInitial(cliente: any): string {
    return (cliente?.nome || '?').charAt(0).toUpperCase();
  }

  getTelefone(cliente: any): string {
    return cliente?.telefone || 'Telefone não informado';
  }

  getLojaOrigem(cliente: any): string {
    return cliente?.lojaOrigem?.nome || cliente?.empresaNome || 'Loja de origem';
  }

  getEnderecoPrincipal(endereco: any): string {
    if (!endereco) return 'Endereço não informado';

    const rua = (endereco.logradouro || endereco.endereco || '').toString().trim();
    const numero = (endereco.numero || '').toString().trim();
    const complemento = (endereco.complemento || '').toString().trim();
    const base = [rua || 'Endereço', numero || 's/n'].filter(Boolean).join(', ');

    return complemento ? `${base} - ${complemento}` : base;
  }

  getEnderecoSecundario(endereco: any): string {
    if (!endereco) return '';

    const bairro = (endereco.bairro || '').toString().trim();
    const cidade = (endereco.cidade?.nome || endereco.cidade || '').toString().trim();
    const uf = (endereco.estado?.sigla || endereco.uf || '').toString().trim();
    const cep = (endereco.cep || '').toString().trim();
    const cidadeUf = [cidade, uf].filter(Boolean).join('/');

    return [bairro, cidadeUf, cep].filter(Boolean).join(' - ');
  }

  getEnderecoTipoLabel(endereco: any): string {
    return (endereco?.descricao || endereco?.tipo || endereco?.apelido || '').toString().trim();
  }

  getEnderecoTipoClasse(endereco: any): string {
    const tipo = this.getEnderecoTipoLabel(endereco).toLowerCase();
    if (tipo.includes('casa')) return 'is-home';
    if (tipo.includes('trabalho')) return 'is-work';
    return 'is-other';
  }

  get lojasSemEndereco(): any[] {
    return (this.lojasParticipantes || [])
      .filter((loja: any) => this.valorBooleano(loja?.ativa))
      .slice()
      .sort((a: any, b: any) => {
        const origemA = this.isLojaOrigemCliente(a) ? 0 : 1;
        const origemB = this.isLojaOrigemCliente(b) ? 0 : 1;
        if (origemA !== origemB) return origemA - origemB;

        const retiradaA = this.lojaPodeContinuarSemEndereco(a) ? 0 : 1;
        const retiradaB = this.lojaPodeContinuarSemEndereco(b) ? 0 : 1;
        if (retiradaA !== retiradaB) return retiradaA - retiradaB;
        return (a?.nome || '').localeCompare(b?.nome || '');
      });
  }

  get lojasRetirada(): any[] {
    return this.lojasSemEndereco.filter((loja: any) => this.lojaPodeContinuarSemEndereco(loja));
  }

  lojaPermiteRetirada(loja: any): boolean {
    return this.valorBooleano(loja?.retiradaAtiva);
  }

  lojaPodeContinuarSemEndereco(loja: any): boolean {
    return this.lojaPermiteRetirada(loja) || !this.temConfiguracaoEntrega(loja);
  }

  lojaRequerEndereco(loja: any): boolean {
    return this.temConfiguracaoEntrega(loja) && !this.lojaPermiteRetirada(loja);
  }

  get lojaDestinoVisual(): any {
    return this.lojaDestino || this.lojasSemEndereco.find((loja: any) => {
      return this.isLojaOrigemCliente(loja) && this.lojaPodeContinuarSemEndereco(loja);
    }) || null;
  }

  get podeContinuarLojaSemEndereco(): boolean {
    return !!this.lojaDestinoVisual && this.lojaPodeContinuarSemEndereco(this.lojaDestinoVisual);
  }

  getLojaSemEnderecoStatus(loja: any): string {
    if (this.lojaPodeContinuarSemEndereco(loja)) return 'Retirada disponível';
    return 'Delivery requer endereço';
  }

  getLojaSemEnderecoSubtitulo(loja: any): string {
    return this.isLojaOrigemCliente(loja) ? 'Loja do cliente' : 'Loja participante';
  }

  getLojaSemEnderecoAcao(loja: any): string {
    if (this.preparando && this.isLojaDestino(loja)) return 'Preparando...';
    if (this.isLojaDestino(loja) && this.lojaPodeContinuarSemEndereco(loja)) return 'Continuar';
    if (this.lojaPodeContinuarSemEndereco(loja)) return 'Selecionar para retirada';
    return 'Cadastrar endereço';
  }

  isLojaDestino(loja: any): boolean {
    const lojaId = loja?.id || loja?.empresaId;
    const destinoId = this.lojaDestino?.id || this.lojaDestino?.empresaId;
    if (destinoId) return String(lojaId || '') === String(destinoId);
    return this.isLojaOrigemCliente(loja);
  }

  isLojaSemEnderecoSelecionada(loja: any): boolean {
    return this.isLojaDestino(loja) && this.lojaPodeContinuarSemEndereco(loja);
  }

  getClienteIndiceGlobal(grupoIndex: number, clienteIndex: number): number {
    let indice = clienteIndex;

    for (let i = 0; i < grupoIndex; i++) {
      indice += (this.clientesAgrupados?.[i]?.clientes || []).length;
    }

    return indice;
  }

  getClienteAtalho(indice: number): string {
    return indice >= 0 && indice < 9 ? String(indice + 1) : '';
  }

  isClienteAtivo(indice: number): boolean {
    return this.clienteIndiceAtivo === indice;
  }

  getEnderecoAtalho(indice: number): string {
    return indice >= 0 && indice < 9 ? String(indice + 1) : '';
  }

  isEnderecoAtivo(indice: number): boolean {
    return this.enderecoIndiceAtivo === indice;
  }

  trackByGrupo(index: number, grupo: any): any {
    return grupo?.loja?.id || grupo?.loja?.nome || index;
  }

  trackByCliente(index: number, cliente: any): any {
    return cliente?.contatoId || cliente?.id || `${cliente?.empresaId || 'loja'}-${index}`;
  }

  trackByEndereco(index: number, endereco: any): any {
    return endereco?.enderecoId || endereco?.id || index;
  }

  trackByLoja(index: number, loja: any): any {
    return loja?.id || loja?.empresaId || index;
  }

  private valorBooleano(valor: any): boolean {
    return valor === true || valor === 1 || valor === '1';
  }

  private temConfiguracaoEntrega(loja: any): boolean {
    return loja?.retiradaAtiva !== undefined || loja?.deliveryAtivo !== undefined;
  }

  private isLojaOrigemCliente(loja: any): boolean {
    const lojaId = loja?.id || loja?.empresaId;
    const lojaOrigemId = this.clienteSelecionado?.lojaOrigem?.id || this.clienteSelecionado?.empresaId;
    return String(lojaId || '') === String(lojaOrigemId || '');
  }

  private confirmeClienteOuBusca() {
    const cliente = this.obtenhaClientePorIndice(this.clienteIndiceAtivo);

    if (cliente) {
      this.selecionarCliente.emit(cliente);
      return;
    }

    this.onBuscar();
  }

  private onKeyDownEnderecos(event: KeyboardEvent) {
    if (event.key === 'F4') {
      event.preventDefault();
      this.onTrocarCliente();
      return;
    }

    if (event.altKey && this.selecioneEnderecoPorAtalho(event)) {
      return;
    }

    if (event.key === 'ArrowDown') {
      event.preventDefault();
      this.movaEnderecoAtivo(1);
      return;
    }

    if (event.key === 'ArrowUp') {
      event.preventDefault();
      this.movaEnderecoAtivo(-1);
      return;
    }

    if (event.key === 'Enter') {
      event.preventDefault();
      this.confirmeEndereco();
    }
  }

  private confirmeEndereco() {
    const endereco = this.obtenhaEnderecoPorIndice(this.enderecoIndiceAtivo);
    if (!endereco) return;

    this.selecionarEndereco.emit(endereco);
  }

  private selecioneClientePorAtalho(event: KeyboardEvent): boolean {
    const indice = Number(event.key) - 1;
    if (!Number.isInteger(indice) || indice < 0 || indice > 8) return false;

    const cliente = this.obtenhaClientePorIndice(indice);
    if (!cliente) return false;

    event.preventDefault();
    this.clienteIndiceAtivo = indice;
    this.selecionarCliente.emit(cliente);
    return true;
  }

  private selecioneEnderecoPorAtalho(event: KeyboardEvent): boolean {
    const indice = Number(event.key) - 1;
    if (!Number.isInteger(indice) || indice < 0 || indice > 8) return false;

    const endereco = this.obtenhaEnderecoPorIndice(indice);
    if (!endereco) return false;

    event.preventDefault();
    this.enderecoIndiceAtivo = indice;
    this.selecionarEndereco.emit(endereco);
    return true;
  }

  private movaClienteAtivo(direcao: number) {
    const total = this.totalClientes;
    if (!total) return;

    const indiceAtual = this.clienteIndiceAtivo < 0 ? 0 : this.clienteIndiceAtivo;
    this.clienteIndiceAtivo = (indiceAtual + direcao + total) % total;
    this.roleClienteAtivoParaTela();
  }

  private movaEnderecoAtivo(direcao: number) {
    const total = this.enderecos?.length || 0;
    if (!total) return;

    const indiceAtual = this.enderecoIndiceAtivo < 0 ? 0 : this.enderecoIndiceAtivo;
    this.enderecoIndiceAtivo = (indiceAtual + direcao + total) % total;
    this.roleEnderecoAtivoParaTela();
  }

  private obtenhaClientePorIndice(indice: number): any {
    if (indice < 0) return null;

    const clientes = this.obtenhaClientesPlanos();
    return clientes[indice] || null;
  }

  private obtenhaEnderecoPorIndice(indice: number): any {
    if (indice < 0) return null;

    return (this.enderecos || [])[indice] || null;
  }

  private obtenhaClientesPlanos(): any[] {
    return (this.clientesAgrupados || []).reduce((clientes: any[], grupo: any) => {
      return clientes.concat(grupo?.clientes || []);
    }, []);
  }

  private foqueBusca() {
    if (this.etapaEnderecos) return;
    this.buscaCentralInput?.nativeElement?.focus();
  }

  private roleClienteAtivoParaTela() {
    setTimeout(() => {
      const card = this.clienteCards?.toArray()?.[this.clienteIndiceAtivo];
      card?.nativeElement?.scrollIntoView({block: 'nearest'});
    }, 0);
  }

  private roleEnderecoAtivoParaTela() {
    setTimeout(() => {
      const card = this.enderecoCards?.toArray()?.[this.enderecoIndiceAtivo];
      card?.nativeElement?.scrollIntoView({block: 'nearest'});
    }, 0);
  }
}
